import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material'
import { CancelRounded, Close, Done, UploadFileRounded } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { apiGet, apiPostFormData } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../../../generic/systemValues/systemValueLabel'
import useDateFormatter from '../../../generic/hooks/useDateFormatter/useDateFormatter'
import useGetExtendedColumns from '../../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import { parseMaxFileSizeBytes } from '../../documentRepository/documentRepositoryUploadValidation'
import { buildConnectorOrder } from './connectorOrder'
import ImportConnectorResult from './ImportConnectorResult'
import { readImportResult, VIEW } from './connectorImportResult'
import {
  FABRICATION_ORDER_MAX_LENGTH, hasChosenFile, initialConnectorRow, initialExtendedValue, isRowComplete, toOrderInput
} from './connectorRow'

const IMPORT_PATH = 'fabricationOrderImportConnector/import'
const ACCEPTED_EXTENSIONS = '.csv,.xlsx,.xlsm'
const NO_RESULT = { view: '' }
const CELL_SX = { verticalAlign: 'middle', px: 1.5, py: 2.5 }
// Fixed widths so the row reads as one even block, centred in the dialog.
const WIDTH = { code: '14rem', date: '14rem', priority: '7rem', list: '11rem', text: '11rem', plan: '8rem', fileName: '14rem' }
// Height of an outlined field, so the file button sits on the same line as the inputs.
const FIELD_HEIGHT = '56px'
const OVERLAY_SX = {
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  backgroundColor: (theme) => theme.palette.background.paper + 'CC'
}
const SECONDARY_BUTTON_SX = {
  border: '1px solid rgba(255, 255, 255, 0.23) !important',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08) !important' }
}

/** Same marking as the creation row of the table: the label of a mandatory control. */
const requiredLabel = (mandatory, t_) => mandatory ? t_('Required *') : ''

const TextCell = ({ value, onChange, mandatory, charLimit, numeric = false, t_, testId, width = WIDTH.text }) => (
  <TextField
    value={value}
    onChange={(event) => onChange(event.target.value)}
    label={requiredLabel(mandatory, t_)}
    type={numeric ? 'number' : 'text'}
    InputLabelProps={{ shrink: true }}
    inputProps={{ maxLength: charLimit, 'data-testid': testId }}
    // The character count sits inside the field, so no cell grows a line below and the row stays centred.
    InputProps={charLimit !== undefined
      ? {
          endAdornment: (
            <InputAdornment position='end'>
              <Typography variant='caption' color='text.secondary'>{String(value).length + ' / ' + charLimit}</Typography>
            </InputAdornment>
          )
        }
      : {}}
    sx={{ width }}
  />
)

const BooleanCell = ({ value, onChange, name }) => (
  <IconButton role='checkbox' aria-checked={value} aria-label={name} onClick={() => onChange(!value)}>
    {value ? <Done color='primary' /> : <Close color='error' />}
  </IconButton>
)

const DateCell = ({ value, onChange, mandatory, dateTimeFormat, t_ }) => (
  <DateTimePicker
    sx={{ width: WIDTH.date }}
    label={requiredLabel(mandatory, t_)}
    value={value === '' ? null : value}
    slotProps={{
      actionBar: { actions: ['cancel', 'accept', 'clear'] },
      textField: { variant: 'outlined', InputLabelProps: { shrink: true } }
    }}
    localeText={{ cancelButtonLabel: t_('CANCEL'), okButtonLabel: t_('ACCEPT'), clearButtonLabel: t_('CLEAR') }}
    closeOnSelect={false}
    format={dateTimeFormat}
    ampm={false}
    timeSteps={{ minutes: 1 }}
    onChange={(newValue) => onChange(newValue ?? '')}
  />
)

/** Dropdown of option labels (strings), like the table's `smartSelect`, with nothing chosen at first. */
const ListCell = ({ options, value, onChange, mandatory, t_ }) => (
  <SmartSelect
    selectableOptions={options}
    value={value}
    onChange={(newValue) => onChange(newValue ?? '')}
    label={requiredLabel(mandatory, t_)}
    autoSelectFirst={false}
    disableClearable={Boolean(mandatory)}
    width={WIDTH.list}
  />
)

/** Options of an extended LIST column come from its own `edit.source()` (the options Sun sent). */
const ExtendedListCell = ({ column, value, onChange, t_ }) => {
  const [options, setOptions] = useState([])
  useEffect(() => {
    let active = true
    column.edit.source().then((loaded) => { if (active) setOptions(loaded ?? []) })
    return () => { active = false }
  }, [column])
  return <ListCell options={options} value={value} onChange={onChange} mandatory={column.mandatory} t_={t_} />
}

const ExtendedCell = ({ column, value, onChange, dateTimeFormat, t_ }) => {
  if (column.type === 'boolean') return <BooleanCell value={value === true} onChange={onChange} name={column.name} />
  if (column.type === 'dateTimePicker') {
    return <DateCell value={value} onChange={onChange} mandatory={column.mandatory} dateTimeFormat={dateTimeFormat} t_={t_} />
  }
  if (column.type === 'smartSelect') return <ExtendedListCell column={column} value={value} onChange={onChange} t_={t_} />
  return <TextCell value={value} onChange={onChange} mandatory={column.mandatory} numeric={Boolean(column.numeric)} t_={t_} testId={'connector-input-' + column.field} />
}

const ImportConnectorContent = ({ onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, dismissAlert] = AlertUI()
  const { format } = useDateFormatter()
  const extendedColumns = useGetExtendedColumns('fabrication_order') ?? []
  const [row, setRow] = useState(initialConnectorRow)
  const [clients, setClients] = useState([])
  const [planTypes, setPlanTypes] = useState([])
  const [maxFileBytes, setMaxFileBytes] = useState(() => parseMaxFileSizeBytes(''))
  const [isDragging, setIsDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(NO_RESULT)
  const [importedInThisOpening, setImportedInThisOpening] = useState(false)
  const busyRef = useRef(false)

  useEffect(() => {
    let active = true
    apiGet('clientsBasicData').then((loaded) => { if (active) setClients(loaded ?? []) }).catch(() => {})
    apiGet('getFabricationOrderPlanTypes').then((loaded) => { if (active) setPlanTypes(loaded ?? []) }).catch(() => {})
    apiGet('settings/MAX_FILE_SIZE').then((loaded) => { if (active) setMaxFileBytes(parseMaxFileSizeBytes(loaded?.value)) }).catch(() => {})
    return () => { active = false }
  }, [])

  const setField = useCallback((field, value) => setRow((previous) => ({ ...previous, [field]: value })), [])
  const setExtendedValue = (field, value) => setRow((previous) => ({ ...previous, extendedValues: { ...previous.extendedValues, [field]: value } }))

  const warn = (message) => showAlert({ title: '', severity: 'warning', message })

  // V5, V6, G5: a file that does not qualify is never taken, and the one already chosen stays.
  const takeFiles = (files) => {
    if (files.length === 0) return
    if (files.length > 1) {
      warn(t_('Drop only one file'))
      return
    }
    if (files[0].size > maxFileBytes) {
      warn(t_('File size exceeds the maximum limit'))
      return
    }
    dismissAlert()
    setField('file', files[0])
  }

  const handleFileInput = (event) => {
    takeFiles(Array.from(event.target.files ?? []))
    event.target.value = ''
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setIsDragging(false)
    if (busy) return
    takeFiles(Array.from(event.dataTransfer?.files ?? []))
  }

  const handleImport = async () => {
    if (busyRef.current || !isRowComplete(row, extendedColumns)) return
    busyRef.current = true
    setBusy(true)
    dismissAlert()
    const order = buildConnectorOrder(toOrderInput(row), extendedColumns)
    const formData = new FormData()
    formData.append('file', row.file)
    formData.append('order', JSON.stringify(order))
    let outcome
    try {
      outcome = readImportResult({ resolved: await apiPostFormData(IMPORT_PATH, formData) })
    } catch (error) {
      outcome = readImportResult({ thrown: error })
    }
    busyRef.current = false
    setBusy(false)
    setResult(outcome)
    // RN17: only an imported result empties the row; any other keeps the data and the file.
    if (outcome.view === VIEW.IMPORTED) {
      setImportedInThisOpening(true)
      setRow(initialConnectorRow())
    }
  }

  const planValue = planTypes.some((plan) => plan.code === row.planCode) ? row.planCode : ''
  const canImport = !busy && isRowComplete(row, extendedColumns)
  const title = createDialogTitle({ title: t_('Import File'), icon: UploadFileRounded })

  return (
    <Dialog
      open
      maxWidth='xl'
      // Only the CLOSE button closes it: Escape and a click outside never do (nothing typed is lost by accident).
      onClose={() => {}}
      PaperProps={{
        'data-testid': 'connector-dialog',
        onDragOver: (event) => { event.preventDefault(); if (!busy) setIsDragging(true) },
        onDragLeave: () => setIsDragging(false),
        onDrop: handleDrop,
        sx: (theme) => ({ background: theme.palette.background.color_373636 + '!important', minWidth: '30rem', position: 'relative' })
      }}
    >
      <DialogTitle>
        <Stack spacing={1} alignItems='center' justifyContent='center'>
          {title.icon}
          {title.render}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {AlertElement}
        <TableContainer>
          <Table size='small' sx={{ width: 'auto', mx: 'auto' }}>
            <TableHead>
              <TableRow>
                {extendedColumns.map((column) => <TableCell key={column.field} align='center'>{column.name}</TableCell>)}
                <TableCell align='center'>{t_('Fabrication Order')}</TableCell>
                <TableCell align='center'>{t_('Target date')}</TableCell>
                <TableCell align='center'>{t_('Priority')}</TableCell>
                <TableCell align='center'>{t_('Client code')}</TableCell>
                <TableCell align='center'>{t_('Plan')}</TableCell>
                <TableCell align='center'>{t_('File')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow data-testid='connector-row'>
                {extendedColumns.map((column) => (
                  <TableCell key={column.field} align='center' sx={CELL_SX} data-testid={'connector-cell-' + column.field}>
                    <ExtendedCell
                      column={column}
                      value={row.extendedValues[column.field] ?? initialExtendedValue(column)}
                      onChange={(value) => setExtendedValue(column.field, value)}
                      dateTimeFormat={format.dateTime}
                      t_={t_}
                    />
                  </TableCell>
                ))}
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-code'>
                  <TextCell value={row.code} onChange={(value) => setField('code', value)} mandatory charLimit={FABRICATION_ORDER_MAX_LENGTH} t_={t_} testId='connector-input-code' width={WIDTH.code} />
                </TableCell>
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-targetDate'>
                  <DateCell value={row.targetDate} onChange={(value) => setField('targetDate', value)} mandatory dateTimeFormat={format.dateTime} t_={t_} />
                </TableCell>
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-priority'>
                  <TextCell value={row.priority} onChange={(value) => setField('priority', value)} mandatory numeric t_={t_} testId='connector-input-priority' width={WIDTH.priority} />
                </TableCell>
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-clientCode'>
                  <ListCell options={clients.map((client) => client.code)} value={row.clientCode} onChange={(value) => setField('clientCode', value)} mandatory={false} t_={t_} />
                </TableCell>
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-plan'>
                  <TextField
                    select
                    value={planValue}
                    onChange={(event) => setField('planCode', event.target.value)}
                    label={requiredLabel(true, t_)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: WIDTH.plan }}
                  >
                    {planTypes.map((plan) => <MenuItem key={plan.id} value={plan.code}>{systemValueCodeLabel(t_, 'fabrication_order_plan_type', plan.code)}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell align='center' sx={CELL_SX} data-testid='connector-cell-file'>
                  {/* The chosen name hangs below the button without adding height, so the row stays centred. */}
                  <Stack alignItems='center' justifyContent='center' sx={{ height: FIELD_HEIGHT, position: 'relative' }}>
                    <Button component='label' variant='outlined' disabled={busy} sx={SECONDARY_BUTTON_SX}>
                      <Typography sx={{ color: 'lightgrey' }}>{t_('Select a file')}</Typography>
                      <input type='file' hidden accept={ACCEPTED_EXTENSIONS} onChange={handleFileInput} data-testid='connector-file-input' />
                    </Button>
                    <Typography
                      variant='caption'
                      noWrap
                      title={hasChosenFile(row) ? row.file.name : ''}
                      sx={{ position: 'absolute', top: '100%', maxWidth: WIDTH.fileName }}
                      data-testid='connector-file-name'
                    >
                      {hasChosenFile(row) ? row.file.name : ''}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <ImportConnectorResult result={result} />
      </DialogContent>
      <DialogActions>
        <Stack spacing={1} direction='row' alignItems='center'>
          <Button variant='outlined' sx={SECONDARY_BUTTON_SX} disabled={busy} onClick={() => onClose(importedInThisOpening)}>
            <CancelRounded sx={{ color: 'lightgrey !important', fontSize: '1.4em', marginRight: '0.3rem' }} />
            <Typography sx={{ color: 'lightgrey' }}>{t_('CLOSE')}</Typography>
          </Button>
          <Button variant='outlined' disabled={!canImport} onClick={handleImport}>
            <Typography>{t_('IMPORT FILE')} </Typography>
            <UploadFileRounded sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
        </Stack>
      </DialogActions>
      {isDragging && !busy && (
        <Box sx={{ ...OVERLAY_SX, pointerEvents: 'none' }}>
          <Typography variant='h6'>{t_('Drop the file to attach it')}</Typography>
        </Box>
      )}
      {busy && (
        <Box sx={OVERLAY_SX} data-testid='connector-busy-overlay'>
          <CircularProgress size={24} />
          <Typography variant='h6'>{t_('Importing the file. This can take a while')}</Typography>
        </Box>
      )}
    </Dialog>
  )
}

/**
 * Dialog of the import connector (story 194). Every opening starts from scratch: the content is mounted only while open.
 * @param {Object} props
 * @param {boolean} props.open whether the dialog is shown
 * @param {(importedInThisOpening: boolean) => void} props.onClose called by CLOSE; true when a load ended `imported` in this opening
 */
const ImportConnectorDialog = ({ open, onClose }) => open ? <ImportConnectorContent onClose={onClose} /> : null

export default ImportConnectorDialog
